/** @format */
import { useContext, useEffect, useState } from 'react';

import { MarketContext } from '@toucan/client/global/market';

import {
    PageData,
    QueryVariables,
    getPageData,
} from '@toucan/client/core/contentful';

import { useMounted } from '@toucan/client/shared/hooks/use-mounted';

type QueryVariablesWithoutLocale = Omit<QueryVariables, 'locale'>;

export function usePageData<V = QueryVariablesWithoutLocale>(
    queryVariables: V & QueryVariablesWithoutLocale,
    fallbackContent?: PageData
): { pageData: PageData; loading: boolean } {
    const { activeMarket } = useContext(MarketContext);
    const [pageData, setPageData] = useState<PageData>({});
    const [loading, setLoading] = useState(true);

    const isMounted = useMounted();

    useEffect(() => {
        (async () => {
            try {
                const contentfulResponse = await getPageData<V>({
                    ...queryVariables,
                    locale: activeMarket.iso,
                });

                // component has been unmounted since we made the request
                if (!isMounted()) {
                    return;
                }

                setPageData(contentfulResponse);
            } catch (error) {
                // set fallback content if available
                if (fallbackContent && isMounted()) {
                    setPageData(fallbackContent);
                }
            }
            setLoading(false);
        })();
    }, []);

    return { pageData, loading };
}
